import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ExternalLink, X } from "lucide-react";
import { useState } from "react";
import { isInAppBrowser, getBrowserName } from "@/utils/browserDetection";

interface InAppBrowserBannerProps {
  currentUrl?: string;
}

const InAppBrowserBanner = ({ currentUrl = window.location.href }: InAppBrowserBannerProps) => {
  const [isDismissed, setIsDismissed] = useState(false);

  if (!isInAppBrowser() || isDismissed) {
    return null;
  }

  const browserName = getBrowserName();

  const handleOpenExternal = () => {
    // Copy URL to clipboard as fallback
    navigator.clipboard?.writeText(currentUrl);
    
    // Try to open in external browser (works on some platforms)
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isIOS) {
      // On iOS, suggest Safari
      alert(`To view this page properly:\n\n1. Tap the three dots (•••) in the top right\n2. Select "Open in Safari"\n\nThe URL has been copied to your clipboard.`);
    } else {
      // On Android, suggest Chrome
      alert(`To view this page properly:\n\n1. Tap the three dots (⋮) in the top right\n2. Select "Open in Chrome" or "Open in browser"\n\nThe URL has been copied to your clipboard.`);
    }
  };

  return (
    <div className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <Alert className="rounded-none border-0 border-b">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <AlertDescription className="text-sm">
              For the best experience, please open this page in Safari or Chrome instead of {browserName}.
            </AlertDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenExternal}
              className="whitespace-nowrap"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in Browser
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsDismissed(true)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default InAppBrowserBanner;
